/**
 * Card variant picker custom element that extends the VariantRadios class from the theme.
 * Handles variant selection for product cards with image switching functionality.
 */
class CardVariantPicker extends VariantRadios {
  constructor() {
    super();

    // Add event listeners for hover and click image switching
    this.initImageSwitching();

    // Initialize overflow detection and scroll controls
    this.initOverflowDetection();

    // Initialize the form with first available variant
    this.initializeVariantSelection();
  }

  /**
   * Initialize the component by selecting the first available variant
   * and updating the form and button states accordingly
   */
  initializeVariantSelection() {
    // Update options and master ID based on current selection
    this.updateOptions();
    this.updateMasterId();

    // Update form state if we have a current variant
    if (this.currentVariant) {
      this.updateFormVariantInput(this.currentVariant.id);
      this.updateAddToCartButton(this.currentVariant.available);
    }
  }

  initOverflowDetection() {
    // Use matchMedia for proper responsive handling
    const mobileMediaQuery = window.matchMedia('(max-width: 749px)');

    const handleMediaChange = (e) => {
      if (e.matches) {
        this.setupOverflowDetection();
      } else {
        this.cleanupOverflowDetection();
      }
    };

    // Initial check
    if (mobileMediaQuery.matches) {
      this.setupOverflowDetection();
    }

    // Listen for media query changes
    mobileMediaQuery.addListener(handleMediaChange);

    // Store reference for cleanup
    this.mobileMediaQuery = mobileMediaQuery;
    this.handleMediaChange = handleMediaChange;
  }

  setupOverflowDetection() {
    // Get the wrapper that contains fieldset and buttons (the actual scrolling element)
    this.scrollableWrapper = this.querySelector('.variant-swatches-wrapper');
    if (!this.scrollableWrapper) return;

    // Get references to existing buttons
    this.leftButton = this.querySelector('.variant-scroll-btn--left');
    this.rightButton = this.querySelector('.variant-scroll-btn--right');

    // Add click listeners to buttons
    if (this.leftButton) {
      this.leftButton.addEventListener('click', () => this.scrollToLeft());
    }
    if (this.rightButton) {
      this.rightButton.addEventListener('click', () => this.scrollToRight());
    }

    // Check overflow state initially
    this.checkOverflowState();

    // Create debounced version of overflow check for scroll events
    this.debouncedOverflowCheck = this.debounce(() => {
      this.checkOverflowState();
    }, 100); // 100ms debounce

    // Add scroll listener to wrapper with debounced check
    this.scrollableWrapper.addEventListener('scroll', this.debouncedOverflowCheck);

    // Add resize observer to handle dynamic content changes
    if (!this.resizeObserver) {
      this.resizeObserver = new ResizeObserver(() => {
        // Use requestAnimationFrame for resize observer to avoid layout thrashing
        requestAnimationFrame(() => {
          this.checkOverflowState();
        });
      });
      this.resizeObserver.observe(this);

      // Also observe the wrapper content
      if (this.scrollableWrapper) {
        this.resizeObserver.observe(this.scrollableWrapper);
      }
    }
  }

  cleanupOverflowDetection() {
    // Remove event listeners from buttons
    if (this.leftButton) {
      this.leftButton.removeEventListener('click', () => this.scrollToLeft());
    }
    if (this.rightButton) {
      this.rightButton.removeEventListener('click', () => this.scrollToRight());
    }

    // Remove scroll listener from wrapper
    if (this.debouncedOverflowCheck && this.scrollableWrapper) {
      this.scrollableWrapper.removeEventListener('scroll', this.debouncedOverflowCheck);
    }

    // Disconnect resize observer
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    // Remove overflow classes from wrapper and hide buttons
    if (this.scrollableWrapper) {
      this.scrollableWrapper.classList.remove('has-left-overflow', 'has-right-overflow');
    }
    if (this.leftButton) this.leftButton.style.display = 'none';
    if (this.rightButton) this.rightButton.style.display = 'none';
  }

  scrollToLeft() {
    if (!this.scrollableWrapper) return;
    // Scroll by approximately one item width
    const scrollAmount = this.scrollableWrapper.clientWidth * 0.6; // Scroll 60% of visible width
    this.scrollableWrapper.scrollBy({
      left: -scrollAmount,
      behavior: 'smooth',
    });
  }

  scrollToRight() {
    if (!this.scrollableWrapper) return;
    // Scroll by approximately one item width
    const scrollAmount = this.scrollableWrapper.clientWidth * 0.6; // Scroll 60% of visible width
    this.scrollableWrapper.scrollBy({
      left: scrollAmount,
      behavior: 'smooth',
    });
  }

  checkOverflowState() {
    // Get the wrapper (the actual scrolling element)
    const wrapper = this.scrollableWrapper || this.querySelector('.variant-swatches-wrapper');
    if (!wrapper) return;

    // Force a reflow to get accurate measurements
    wrapper.offsetHeight;

    // Get scroll position explicitly as property from wrapper
    const currentScrollLeft = wrapper.scrollLeft;
    const scrollWidth = wrapper.scrollWidth;
    const clientWidth = wrapper.clientWidth;

    // Check if there's any overflow at all
    const hasAnyOverflow = scrollWidth > clientWidth;

    if (!hasAnyOverflow) {
      // No overflow at all, hide everything
      wrapper.classList.remove('has-left-overflow', 'has-right-overflow');
      if (this.leftButton) this.leftButton.style.display = 'none';
      if (this.rightButton) this.rightButton.style.display = 'none';
      return;
    }

    // Check if there's content hidden to the left
    const hasLeftOverflow = currentScrollLeft > 1; // Small tolerance

    // Check if there's content hidden to the right
    const hasRightOverflow = currentScrollLeft < scrollWidth - clientWidth - 1;

    // Update classes on wrapper
    wrapper.classList.toggle('has-left-overflow', hasLeftOverflow);
    wrapper.classList.toggle('has-right-overflow', hasRightOverflow);

    // Update button visibility
    if (this.leftButton) {
      this.leftButton.style.display = hasLeftOverflow ? 'flex' : 'none';
    }
    if (this.rightButton) {
      this.rightButton.style.display = hasRightOverflow ? 'flex' : 'none';
    }
  }

  handleScroll() {
    this.checkOverflowState();
  }

  // Debounce utility function for performance optimization
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func.apply(this, args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  disconnectedCallback() {
    // Clean up when element is removed from DOM
    this.cleanupOverflowDetection();

    if (this.mobileMediaQuery && this.handleMediaChange) {
      this.mobileMediaQuery.removeListener(this.handleMediaChange);
    }
  }

  initImageSwitching() {
    // Add click listeners to all swatch elements (hover functionality removed)
    const swatches = this.querySelectorAll('label.color__swatch');

    swatches.forEach((swatch) => {
      swatch.addEventListener('click', this.onSwatchClick.bind(this));
    });
  }

  onSwatchClick(event) {
    // Prevent the default label click behavior to avoid double triggering
    event.preventDefault();

    const swatchLabel = event.currentTarget;
    // Get the input that this label is for
    const inputId = swatchLabel.getAttribute('for');
    const input = document.getElementById(inputId);

    if (!input) return;

    const cardWrapper = this.closest('.card-wrapper');

    // Always allow selection - no deselection functionality
    this.currentSelectedInput = input;

    // Set flag to indicate permanent selection
    if (cardWrapper) {
      cardWrapper.dataset.selectedVariantImage = 'true';
    }

    // Uncheck all other inputs first
    this.querySelectorAll('input[data-option-value]').forEach((otherInput) => {
      otherInput.checked = false;
    });

    // Check the clicked input
    input.checked = true;

    // Update the variant state
    this.updateOptions();
    this.updateMasterId();

    // Update the image with permanent selection
    this.updateCardImage(input, true);

    // Update the variant label text
    this.updateVariantLabel();

    // Update form and button state for selection
    if (this.currentVariant) {
      this.updateFormVariantInput(this.currentVariant.id);
      this.updateAddToCartButton(this.currentVariant.available);
    }
  }

  /**
   * Restore variant change functionality for form handling while preventing unwanted side effects.
   * This allows proper form updates for add to cart functionality.
   */
  onVariantChange(event) {
    // Update our basic variant tracking
    this.updateOptions();
    this.updateMasterId();

    // Handle form updates for add to cart functionality
    if (this.currentVariant) {
      this.updateFormVariantInput(this.currentVariant.id);
      this.updateAddToCartButton(this.currentVariant.available);
    } else {
      this.updateFormVariantInput(null);
      this.updateAddToCartButton(false);
    }

    // Don't call super.onVariantChange() to avoid unwanted side effects like URL updates
  }

  /**
   * Update the form's variant ID input to match the selected variant
   */
  updateFormVariantInput(variantId) {
    const cardWrapper = this.closest('.card-wrapper');
    if (!cardWrapper) return;

    const form = cardWrapper.querySelector('form.product-form');
    if (!form) return;

    const variantInput = form.querySelector('input[name="id"]');
    if (!variantInput) return;

    if (variantId) {
      variantInput.value = variantId;
    } else {
      // Reset to first available variant when deselected
      const variants = this.getVariantData();
      const firstAvailable = variants.find((variant) => variant.available);
      variantInput.value = firstAvailable ? firstAvailable.id : variants[0]?.id || '';
    }

    // Also update the add-to-cart element's data-variant-id attribute
    const addButton = form.querySelector('add-to-cart');
    if (addButton && variantId) {
      addButton.setAttribute('data-variant-id', variantId);
    }

    // Trigger change event so other components know the variant changed
    variantInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * Update the add to cart button state based on variant availability
   */
  updateAddToCartButton(available) {
    const cardWrapper = this.closest('.card-wrapper');
    if (!cardWrapper) return;

    const form = cardWrapper.querySelector('form.product-form');
    if (!form) return;

    const addButton = form.querySelector('add-to-cart, .button');
    if (!addButton) return;

    if (available && this.currentVariant) {
      addButton.removeAttribute('disabled');
      addButton.removeAttribute('data-initial-state');

      // Update button text if it's an add-to-cart element
      if (addButton.tagName.toLowerCase() === 'add-to-cart') {
        // Clear existing content
        addButton.innerHTML = '';

        // Add mobile text span
        const mobileText = document.createElement('span');
        mobileText.className = 'btn-text btn-text--mob';
        mobileText.textContent = window.variantStrings?.add || 'Add';
        addButton.appendChild(mobileText);

        // Add desktop text span
        const desktopText = document.createElement('span');
        desktopText.className = 'btn-text btn-text--desk';
        desktopText.textContent = window.variantStrings?.addToCart || 'Add to cart';
        addButton.appendChild(desktopText);

        // Add the cart icon to the desktop span only
        if (window.icons?.arrowRight) {
          desktopText.insertAdjacentHTML('beforeend', ' ' + window.icons.arrowRight);
        }
      }
    } else {
      addButton.setAttribute('disabled', true);

      // Update button text if it's an add-to-cart element
      if (addButton.tagName.toLowerCase() === 'add-to-cart') {
        // Clear existing content
        addButton.innerHTML = '';

        // Add single text span for unavailable/sold out
        const textSpan = document.createElement('span');
        textSpan.className = 'btn-text';

        if (!this.currentVariant) {
          textSpan.textContent = window.variantStrings?.unavailable || 'Unavailable';
        } else {
          textSpan.textContent = window.variantStrings?.soldOut || 'Sold out';
        }

        addButton.appendChild(textSpan);
      }
    }
  }

  /**
   * Update the variant label text to show the selected variant option and extra text.
   */
  updateVariantLabel() {
    if (!this.currentVariant) return;

    const labelInfo = this.querySelector('.form__label-info');

    if (!labelInfo) return;

    // Get the selected option value (e.g., "nude" or "honey")
    const selectedOptionValue = this.currentVariant.options[0];

    // Check if variant has extra text metafield
    const extraText = this.currentVariant.metafields?.custom?.['variant-extra-text'];

    if (extraText) {
      labelInfo.textContent = `– ${selectedOptionValue} | ${extraText}`;
    } else {
      labelInfo.textContent = `– ${selectedOptionValue}`;
    }
  }

  /**
   * Override the update method to add custom functionality.
   * Get the inputs from the fieldsets and update the options array.
   */
  updateOptions() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset'));

    this.options = fieldsets.map((fieldset) => {
      const inputs = fieldset.querySelectorAll('input');
      const selectedInput = Array.from(inputs).find((radio) => radio.checked);

      return selectedInput?.value;
    });
  }

  /**
   * Override updateMasterId to add debugging and ensure proper variant matching
   */
  updateMasterId() {
    const variants = this.getVariantData();

    this.currentVariant = variants?.find((variant) => {
      return !variant.options
        .map((option, index) => {
          return this.options[index] === option;
        })
        .includes(false);
    });
  }

  updateCardImage(input, isPermanent = false) {
    // Get the card wrapper container
    const cardWrapper = this.closest('.card-wrapper');
    if (!cardWrapper) {
      return;
    }

    const image = cardWrapper.querySelector('.card__media img.motion-reduce, .image-animate img.motion-reduce');

    if (!image) {
      return;
    }

    // Store original image data if not already stored
    if (!cardWrapper.dataset.originalSrc) {
      cardWrapper.dataset.originalSrc = image.src;
      cardWrapper.dataset.originalSrcset = image.srcset || '';
      cardWrapper.dataset.originalAlt = image.alt;
    }

    // Find the variant based on the selected input
    const optionValue = input.dataset.optionValue || input.value;

    const variants = this.getVariantData();

    const variant = variants.find(
      (variant) => variant.option1 && variant.option1.toLowerCase() === optionValue.toLowerCase()
    );

    if (variant) {
      // Try to get image from variant media data first
      const variantMediaData = this.getVariantMediaData(cardWrapper);

      let imageData = null;

      if (variantMediaData) {
        const mediaInfo = variantMediaData.find((v) => v.variantId === variant.id.toString());

        if (mediaInfo && mediaInfo.media && mediaInfo.media[0]) {
          imageData = {
            src: mediaInfo.media[0].imageUrl,
            alt: mediaInfo.media[0].altText,
          };
        }
      }

      // Fall back to featured_image if no media data
      if (!imageData && variant.featured_image) {
        imageData = {
          src: variant.featured_image.src,
          alt: variant.featured_image.alt || variant.name || 'Product variant image',
        };
      }

      if (imageData) {
        image.src = imageData.src;
        image.srcset = this.generateSrcset(imageData.src);
        image.alt = imageData.alt;

        // Mark as permanent if clicked
        if (isPermanent) {
          cardWrapper.dataset.selectedVariantImage = 'true';
        }
      }
    }
  }

  revertToFeaturedImage() {
    const cardWrapper = this.closest('.card-wrapper');
    if (!cardWrapper) return;

    // Always revert when explicitly called (used for deselection)
    const image = cardWrapper.querySelector('.card__media img.motion-reduce, .image-animate img.motion-reduce');
    if (!image || !cardWrapper.dataset.originalSrc) return;

    image.src = cardWrapper.dataset.originalSrc;
    image.srcset = cardWrapper.dataset.originalSrcset;
    image.alt = cardWrapper.dataset.originalAlt;
  }

  getVariantMediaData(cardWrapper) {
    const variantMediaString = cardWrapper.dataset.variantMedia;
    if (!variantMediaString) return null;

    try {
      return JSON.parse(variantMediaString);
    } catch (e) {
      console.error('Error parsing variant media data:', e);
      return null;
    }
  }

  generateSrcset(imageSrc) {
    // Generate responsive srcset based on Shopify image transformations
    if (!imageSrc) return '';

    const baseUrl = imageSrc.split('?')[0];
    return [
      `${baseUrl}?width=165 165w`,
      `${baseUrl}?width=360 360w`,
      `${baseUrl}?width=535 535w`,
      `${baseUrl}?width=750 750w`,
      `${baseUrl}?width=1100 1100w`,
    ].join(', ');
  }
}

// Define the custom element
customElements.define('card-variant-picker', CardVariantPicker);
